import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { addDays } from '../../components/novarium/periodRange';
import { NewBornGranularity, NewBornTimelinePointDTO } from '../../dto/newborn/NewBornDTO';
import { useChartTokens } from '../ServicesStatistics/chartTokens';
import { PeakAnalysis } from '../ServicesStatistics/peaks';
import { escapeHtml, parseIsoDate } from '../ServicesStatistics/statisticsUtils';
import { axisLabel } from '../ServicesStatistics/TimelineChart';
import { formatCount, formatPercent } from '../UrgencySummary/urgencySummaryFormat';
import { isLowVolume, NewBornMeasure, pointBase, pointValue } from './newBornEvolutionModel';
import { periodTitleOf } from './newBornPeriodText';

interface NewBornEvolutionChartProps {
    points: NewBornTimelinePointDTO[];
    granularity: NewBornGranularity;
    measure: NewBornMeasure;
    ariaLabel: string;
    /** Promedio del rango para la línea de referencia (nacimientos por período o % de cesáreas); null: sin línea. */
    average: number | null;
    /** Picos del % de cesáreas, uno por posición de `points`; null con «Nacimientos» (no hay picos). */
    analysis: PeakAnalysis | null;
    /** Click, tap o Enter sobre un período: abre su panel. `opener` es a donde vuelve el foco al cerrarlo. */
    onOpen: (point: NewBornTimelinePointDTO, opener: HTMLElement | null) => void;
}

const CHART_HEIGHT = 280;
const HALF_DAY_MS = 12 * 60 * 60 * 1000;
/** Marcador de un punto común, de un pico y del punto resaltado con el teclado. */
const MARKER_SIZE = 3;
const PEAK_MARKER_SIZE = 7;
const ACTIVE_MARKER_SIZE = 8;
/** ApexCharts avisa del click por dos eventos (`markerClick` y `dataPointSelection`): el segundo, dentro de este tiempo, se ignora. */
const DUPLICATE_CLICK_MS = 400;

/** Un punto del eje de tiempo: con `point` es un período dibujado; sin él, un extremo del rango que no tiene valor. */
interface PlotPoint {
    x: number;
    y: number | null;
    point: NewBornTimelinePointDTO | null;
    /** Posición del período en `points` (y en `analysis`). */
    index: number;
}

const periodStart = (point: NewBornTimelinePointDTO): Date => parseIsoDate(point.from);

interface PointFlags {
    peak: boolean;
    lowVolume: boolean;
}

/** El valor de la medida escrito: «14» o «90,5 %». */
function valueText(intl: IntlShape, point: NewBornTimelinePointDTO, measure: NewBornMeasure): string {
    return measure === 'births' ? formatCount(intl, point.births) : formatPercent(intl, point.cesareanPct);
}

/** Tooltip del período: su título, el valor de la medida (con sus partos si es el %) y las notas del período. */
function tooltipHtml(intl: IntlShape, point: NewBornTimelinePointDTO, measure: NewBornMeasure,
                     granularity: NewBornGranularity, flags: PointFlags): string {
    const text = (id: string, values?: Record<string, string | number>) => escapeHtml(intl.formatMessage({ id }, values));
    const lines = [`<div class="nv-newborn-tip__title">${escapeHtml(periodTitleOf(intl, point, granularity))}</div>`];
    if (measure === 'births') {
        lines.push(`<div>${text('NewBornSeriesBirths')}: ${formatCount(intl, point.births)}</div>`);
    } else {
        lines.push(`<div>${text('NewBornSeriesCesarean')}: ${escapeHtml(valueText(intl, point, measure))}</div>`);
        lines.push(`<div>${text('NewBornKpiOfDeliveries', {
            count: formatCount(intl, point.cesareans), total: formatCount(intl, point.cesareanBase),
        })}</div>`);
    }
    if (flags.peak) lines.push(`<div class="nv-newborn-tip__note nv-newborn-tip__note--peak">${text('NewBornTipPeak')}</div>`);
    if (point.partial) lines.push(`<div class="nv-newborn-tip__note">${text('NewBornTipPartial')}</div>`);
    else if (flags.lowVolume) lines.push(`<div class="nv-newborn-tip__note">${text('NewBornTipLow')}</div>`);
    lines.push(`<div class="nv-newborn-tip__note">${text('NewBornTipOpen')}</div>`);
    return `<div class="nv-newborn-tip">${lines.join('')}</div>`;
}

/**
 * Evolución por semana o mes: una línea con un punto por período, de los nacimientos o del % de cesáreas. El eje X es un eje
 * de tiempo real: ApexCharts ralea solo los rótulos para que no se pisen. Una línea punteada marca el promedio del rango
 * (ponderado por la base, no un promedio de promedios). Los períodos parciales (recortados por el rango) y, con el %, los de
 * pocos partos llevan un marcador hueco: su valor puede no ser representativo. Un período sin partos no tiene % y no se dibuja
 * (no cuenta como 0 %); en nacimientos, sí vale cero. Los picos del % llevan un marcador más grande con su etiqueta. Un
 * click, un tap o Enter sobre un período abre su panel; con el teclado, el contenedor es un grupo enfocable: flechas e
 * Inicio/Fin mueven un punto resaltado y una región `aria-live` dice dónde está (el mismo patrón que la evolución de
 * Atenciones de urgencia).
 * TODO(design-system): gráficos. Novarium no define el estilo de los gráficos.
 * TODO(design-system): color de pico en gráficos. Se usa el par --warn / --warn-bg de la etiqueta «Atención» del ranking.
 */
export default function NewBornEvolutionChart({
    points, granularity, measure, ariaLabel, average, analysis, onOpen,
}: NewBornEvolutionChartProps) {
    const intl = useIntl();
    const ref = useRef<HTMLDivElement>(null);
    const hintId = useId();
    const tokens = useChartTokens(ref);
    const isBirths = measure === 'births';
    /** Posición en `plot` del período resaltado con el teclado (apuntarlo con el mouse lo resuelve el tooltip). */
    const [activePlot, setActivePlot] = useState<number | null>(null);
    const lastOpenRef = useRef(0);

    /**
     * Lo que se dibuja: los períodos con valor. Se agregan puntos sin valor en los extremos del rango, para que el eje de
     * tiempo abarque todo el rango y ApexCharts no lo achique a los puntos dibujados (con uno solo no sabe dibujar los
     * rótulos del eje); con un solo período se suma un día a cada lado.
     */
    const plot = useMemo<PlotPoint[]>(() => {
        if (points.length === 0) return [];
        const drawn: PlotPoint[] = points.flatMap((point, index) => {
            const y = pointValue(point, measure);
            return y !== null ? [{ x: periodStart(point).getTime(), y, point, index }] : [];
        });
        const spare = points.length === 1 ? 1 : 0;
        const lowX = addDays(periodStart(points[0]), -spare).getTime();
        const highX = addDays(parseIsoDate(points[points.length - 1].to), spare).getTime();
        if (drawn.length === 0 || drawn[0].x > lowX) drawn.unshift({ x: lowX, y: null, point: null, index: -1 });
        if (drawn[drawn.length - 1].x < highX) drawn.push({ x: highX, y: null, point: null, index: -1 });
        return drawn;
    }, [points, measure]);

    const series = useMemo(() => [{
        name: intl.formatMessage({ id: isBirths ? 'NewBornSeriesBirths' : 'NewBornSeriesCesarean' }),
        data: plot.map(({ x, y }) => ({ x, y })),
    }], [intl, plot, isBirths]);

    const flagsOf = useCallback((index: number): PointFlags => ({
        peak: analysis?.peaks.includes(index) ?? false,
        lowVolume: isLowVolume(points[index], measure),
    }), [analysis, points, measure]);

    /** Los períodos a los que se llega con las flechas: los dibujados. */
    const navigable = useMemo(() => plot.flatMap((item, position) => (item.point ? [position] : [])), [plot]);

    useEffect(() => { setActivePlot(null); }, [plot]);

    /** Abre el panel del período en la posición `position` de `plot`. */
    const openAt = useCallback((position: number) => {
        const point = plot[position]?.point;
        if (!point) return;
        const now = Date.now();
        if (now - lastOpenRef.current < DUPLICATE_CLICK_MS) return;
        lastOpenRef.current = now;
        setActivePlot(position);
        onOpen(point, ref.current);
    }, [plot, onOpen]);

    const options = useMemo<ApexOptions | null>(() => {
        if (!tokens || plot.length === 0 || !ref.current) return null;
        const rangeStart = periodStart(points[0]).getTime();
        const rangeEnd = parseIsoDate(points[points.length - 1].to).getTime();
        const warnBg = getComputedStyle(ref.current).getPropertyValue('--warn-bg').trim();
        const font = { fontFamily: tokens.font, fontSize: '12px' };

        // Marcadores huecos: períodos parciales y, con el %, de pocos partos
        const hollow = plot.flatMap((item, position) => (item.point && isLowVolume(item.point, measure)
            ? [{
                seriesIndex: 0, dataPointIndex: position, fillColor: tokens.card, strokeColor: tokens.primary,
                size: MARKER_SIZE, shape: 'circle' as const,
            }] : []));

        const peakAnnotations = plot.flatMap((item) => {
            if (!item.point || item.y === null || !analysis?.peaks.includes(item.index)) return [];
            return [{
                x: item.x,
                y: item.y,
                marker: { size: PEAK_MARKER_SIZE, fillColor: tokens.warn, strokeColor: warnBg, strokeWidth: 2 },
                label: {
                    text: `${axisLabel(intl, item.x, granularity)} · ${formatPercent(intl, item.y)}`,
                    borderColor: tokens.warn,
                    borderWidth: 1,
                    offsetY: -4,
                    style: { ...font, color: tokens.warn, background: warnBg, fontWeight: 600 },
                },
            }];
        });

        const activeItem = activePlot === null ? null : plot[activePlot];
        const activeAnnotations = activeItem && activeItem.point && activeItem.y !== null ? [{
            x: activeItem.x,
            y: activeItem.y,
            marker: { size: ACTIVE_MARKER_SIZE, fillColor: tokens.card, strokeColor: tokens.textStrong, strokeWidth: 2 },
        }] : [];

        const averageText = average === null ? '' : isBirths
            ? intl.formatMessage({ id: granularity === 'WEEK' ? 'NewBornAverageBirthsWeek' : 'NewBornAverageBirthsMonth' },
                { value: formatCount(intl, Math.round(average)) })
            : intl.formatMessage({ id: 'NewBornAverageCesarean' }, { value: formatPercent(intl, average) });

        return {
            chart: {
                type: 'line',
                height: CHART_HEIGHT,
                fontFamily: tokens.font,
                foreColor: tokens.text2,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
                events: {
                    // Con el tooltip compartido ApexCharts avisa del click con el período más cercano, no hace falta acertarle al punto
                    markerClick: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
                    dataPointSelection: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
                },
            },
            colors: [tokens.primary],
            states: { active: { filter: { type: 'none' } } },
            stroke: { width: 2, curve: 'straight', lineCap: 'round' },
            markers: {
                size: MARKER_SIZE,
                strokeWidth: 2,
                strokeColors: [tokens.primary],
                discrete: hollow,
            },
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: { borderColor: tokens.divider, strokeDashArray: 0 },
            annotations: {
                yaxis: average !== null ? [{
                    y: average,
                    borderColor: tokens.text2,
                    strokeDashArray: 4,
                    label: {
                        text: averageText,
                        position: 'left',
                        textAnchor: 'start',
                        offsetX: 4,
                        borderWidth: 0,
                        style: { ...font, color: tokens.text2, background: 'transparent' },
                    },
                }] : [],
                points: [...peakAnnotations, ...activeAnnotations],
            },
            xaxis: {
                type: 'datetime',
                axisBorder: { color: tokens.divider },
                axisTicks: { color: tokens.divider },
                tooltip: { enabled: false },
                labels: {
                    rotate: 0,
                    datetimeUTC: false,
                    hideOverlappingLabels: true,
                    showDuplicates: false,
                    style: { fontSize: '12px' },
                    // Con un solo punto ApexCharts no pasa la marca de tiempo: el valor es la marca misma
                    formatter: (value: string, timestamp?: number) => {
                        const time = timestamp ?? Number(value);
                        // Los días vacíos de los extremos (ver `plot`) no llevan rótulo: no son parte del rango
                        if (!Number.isFinite(time) || time < rangeStart - HALF_DAY_MS || time > rangeEnd + HALF_DAY_MS) return '';
                        return axisLabel(intl, time, granularity);
                    },
                },
            },
            yaxis: {
                min: 0,
                ...(isBirths ? {} : { max: 100 }),
                forceNiceScale: true,
                labels: {
                    style: { fontSize: '12px' },
                    formatter: (value: number) => (isBirths ? formatCount(intl, Math.round(value)) : `${formatCount(intl, Math.round(value))} %`),
                },
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
                    // Un punto sin valor es un extremo del rango: se muestra el período dibujado más cercano
                    const item = plot[dataPointIndex]?.point
                        ? plot[dataPointIndex]
                        : (dataPointIndex <= 0 ? plot.find((candidate) => candidate.point) : [...plot].reverse().find((candidate) => candidate.point));
                    return item?.point ? tooltipHtml(intl, item.point, measure, granularity, flagsOf(item.index)) : '';
                },
            },
        };
    }, [intl, tokens, plot, points, isBirths, measure, granularity, average, analysis, activePlot, openAt, flagsOf]);

    // ── Teclado ──

    /** Lo que se dice (y se ve) de un período: «Semana del 14/09 al 20/09: 14 bebés. Pico.» */
    const describe = (position: number): string => {
        const item = plot[position];
        if (!item?.point) return '';
        const point = item.point;
        const period = periodTitleOf(intl, point, granularity);
        const flags = flagsOf(item.index);
        const parts = [
            isBirths
                ? intl.formatMessage({ id: 'NewBornLiveBirths' }, { period, count: point.births, countText: formatCount(intl, point.births) })
                : intl.formatMessage({ id: 'NewBornLiveCesarean' }, {
                    period,
                    value: valueText(intl, point, measure),
                    count: formatCount(intl, point.cesareans),
                    total: formatCount(intl, pointBase(point, measure)),
                }),
            flags.peak ? intl.formatMessage({ id: 'NewBornLivePeak' }) : '',
            point.partial ? intl.formatMessage({ id: 'NewBornLivePartial' }) : flags.lowVolume ? intl.formatMessage({ id: 'NewBornLiveLow' }) : '',
        ];
        return parts.filter(Boolean).join(' ');
    };

    const move = (event: KeyboardEvent<HTMLDivElement>) => {
        if (navigable.length === 0) return;
        const current = activePlot === null ? -1 : navigable.indexOf(activePlot);
        let next: number | null = null;
        if (event.key === 'ArrowRight') next = Math.min(navigable.length - 1, current + 1);
        else if (event.key === 'ArrowLeft') next = current < 0 ? navigable.length - 1 : Math.max(0, current - 1);
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = navigable.length - 1;
        else if ((event.key === 'Enter' || event.key === ' ') && activePlot !== null) {
            event.preventDefault();
            openAt(activePlot);
            return;
        }
        if (next !== null) {
            event.preventDefault();
            setActivePlot(navigable[next]);
        }
    };

    const activeText = activePlot === null ? '' : describe(activePlot);

    return (
        <div className="nv-newborn-timeline">
            <div
                ref={ref}
                className="nv-newborn-chart"
                role="group"
                aria-label={ariaLabel}
                aria-roledescription={intl.formatMessage({ id: 'NewBornChartRole' })}
                aria-describedby={hintId}
                tabIndex={0}
                onKeyDown={move}
                onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActivePlot(null); }}
            >
                <div aria-hidden="true">
                    {options && (
                        <ReactApexChart key={`${measure}-${granularity}`} type="line" height={CHART_HEIGHT} series={series} options={options} />
                    )}
                </div>
                <span id={hintId} className="nv-sr-only">{intl.formatMessage({ id: 'NewBornChartHint' })}</span>
                <span className="nv-sr-only" role="status" aria-live="polite">{activeText}</span>
            </div>
            {/* Solo el período resaltado con el teclado; el lector de pantalla lo oye por la región `aria-live` de arriba */}
            {activeText && <p className="nv-newborn-timeline__detail nv-help" aria-hidden="true">{activeText}</p>}
        </div>
    );
}
